import { ChannelConfigSchema, type ChannelConfig, type TenantSettings } from '@lastrouter/contracts';
import { z } from 'zod';

/*
 * Produtos que usam o contexto (Chat, Ask no Teams, conectores MCP) e o canal de cada um. Por baixo,
 * continua a configuração de sempre (TenantSettings.channels e limits; o painel vale por cima do arquivo,
 * ADR 0020): estas funções só dizem de que produto é cada canal e como gravar um sem mexer nos outros.
 */

export type Product = 'teams' | 'chat' | 'mcp';

/** Canal do produto: teams é o do bot; o do chat é chat.channel da configuração (web por padrão); o resto é conector MCP. */
export function productOf(channelId: string, chatChannel: string): Product {
  if (channelId === 'teams' || channelId.startsWith('teams-') || channelId.startsWith('teams_')) return 'teams';
  if (channelId === chatChannel) return 'chat';
  return 'mcp';
}

/** Nome do conector para quem usa o painel: Claude, Copilot ou "outro". */
export function connectorKind(channelId: string): 'claude' | 'copilot' | 'other' {
  if (channelId.startsWith('claude')) return 'claude';
  if (channelId.startsWith('copilot')) return 'copilot';
  return 'other';
}

/** Canais do arquivo, como a página Configurações mostra (sem segredos). Cloud não mostra: lista vazia. */
export function fileChannels(config: Record<string, unknown> | undefined): ChannelConfig[] {
  const r = z.array(ChannelConfigSchema).safeParse(config?.channels ?? []);
  return r.success ? r.data : [];
}

/** O que vale hoje: os canais do painel, se existem; senão os do arquivo. */
export const effectiveChannels = (settings: TenantSettings, file: ChannelConfig[]): ChannelConfig[] => settings.channels ?? file;

/**
 * Configuração nova depois de salvar UM canal na tela de um produto. Os canais dos outros produtos ficam
 * como estão. Se o painel ainda não tinha canais, ele passa a valer começando por uma cópia do arquivo
 * (só quando o canal mudou: mexer só no limite não troca a origem).
 */
export function saveChannel(
  settings: TenantSettings,
  file: ChannelConfig[],
  original: ChannelConfig | null,
  edited: ChannelConfig,
  limit: number | null,
): TenantSettings {
  const base = effectiveChannels(settings, file);
  const changed = !original || JSON.stringify(original) !== JSON.stringify(edited);
  const channels = changed ? (original ? base.map((c) => (c.id === original.id ? edited : c)) : [...base, edited]) : settings.channels;
  const per_channel = { ...settings.limits.per_channel };
  if (original) delete per_channel[original.id];
  if (limit !== null) per_channel[edited.id] = limit;
  return { ...settings, channels, limits: { ...settings.limits, per_channel } };
}

/** Tira um canal (e o limite dele), mantendo os outros. */
export function removeChannel(settings: TenantSettings, file: ChannelConfig[], id: string): TenantSettings {
  const per_channel = { ...settings.limits.per_channel };
  delete per_channel[id];
  return { ...settings, channels: effectiveChannels(settings, file).filter((c) => c.id !== id), limits: { ...settings.limits, per_channel } };
}
